import React, { useEffect, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { createLowlight, common } from "lowlight";
import {
  TextB, TextItalic, TextStrikethrough, ListBullets, ListNumbers,
  Quotes, Code, LinkSimple, Image as ImageIcon, TextHOne, TextHTwo, TextHThree,
  Table as TableIcon, Rows, Columns, Trash
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const lowlight = createLowlight(common);

const ToolbarBtn = ({ active, onClick, children, label, disabled = false }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    className={`h-8 w-8 grid place-items-center border border-border hover:bg-secondary transition-colors disabled:opacity-30 disabled:hover:bg-transparent ${active ? "bg-foreground text-background" : ""}`}
    aria-label={label}
    title={label}
    data-testid={`toolbar-${label}`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = "Tell your story..." }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: false }),
      CodeBlockLowlight.configure({ lowlight, defaultLanguage: "plaintext" }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || "",
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: { class: "tiptap focus:outline-none" },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML() && document.activeElement?.classList?.contains("tiptap") === false) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line
  }, [value, editor]);

  const insertImage = useCallback(async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        editor.chain().focus().setImage({ src: fullUrl }).run();
      } catch (_) {}
    };
    input.click();
  }, [editor]);

  const setLink = useCallback(() => {
    const url = window.prompt("URL");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const isTableActive = editor.isActive("table");

  return (
    <div className="border border-border bg-card">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-secondary sticky top-16 z-20">
        <ToolbarBtn label="h1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <TextHOne size={14} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn label="h2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <TextHTwo size={14} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn label="h3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <TextHThree size={14} weight="bold" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn label="bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <TextB size={14} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn label="italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <TextItalic size={14} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn label="strike" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <TextStrikethrough size={14} weight="bold" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn label="bullet" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <ListBullets size={14} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn label="ordered" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListNumbers size={14} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn label="quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quotes size={14} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn label="code" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
          <Code size={14} weight="bold" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn label="link" active={editor.isActive("link")} onClick={setLink}>
          <LinkSimple size={14} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn label="image" onClick={insertImage}>
          <ImageIcon size={14} weight="bold" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn label="Insert Table" active={isTableActive} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
          <TableIcon size={14} weight="bold" />
        </ToolbarBtn>
        {isTableActive && (
          <>
            <ToolbarBtn label="Add Row After" onClick={() => editor.chain().focus().addRowAfter().run()}>
              <Rows size={14} weight="bold" />
            </ToolbarBtn>
            <ToolbarBtn label="Delete Row" onClick={() => editor.chain().focus().deleteRow().run()}>
              <Rows size={14} weight="regular" className="text-destructive" />
            </ToolbarBtn>
            <ToolbarBtn label="Add Column After" onClick={() => editor.chain().focus().addColumnAfter().run()}>
              <Columns size={14} weight="bold" />
            </ToolbarBtn>
            <ToolbarBtn label="Delete Column" onClick={() => editor.chain().focus().deleteColumn().run()}>
              <Columns size={14} weight="regular" className="text-destructive" />
            </ToolbarBtn>
            <ToolbarBtn label="Delete Table" onClick={() => editor.chain().focus().deleteTable().run()}>
              <Trash size={14} weight="bold" />
            </ToolbarBtn>
          </>
        )}
      </div>
      <div className="p-5 min-h-[300px]">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

